import { PropsWithChildren, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { FlexContent, PublicLayout } from "../layout";
import ContentWrapper from "../layout/ContentWrapper";
import Button from "../button/Button";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import ErrorBoundaryForm from "./ErrorBoundaryForm";

interface SentryFallbackRenderProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

const ErrorFallback = ({ eventId, resetError }: SentryFallbackRenderProps) => {
  const { tError } = useCustomTranslation();
  const [showForm, setShowForm] = useState(false);

  // Clear body overflow in case modal left it locked
  // Note: Don't manually remove modal-root children - let React handle portal cleanup
  // to avoid "insertBefore" errors when React tries to reconcile DOM state
  useEffect(() => {
    document.body.style.overflow = "";
  }, []);

  const handleGoHome = () => {
    resetError();
    setTimeout(() => {
      window.location.href = "/";
    }, 1000);
  };

  if (showForm) {
    return <ErrorBoundaryForm resetError={resetError} eventId={eventId} />;
  }

  return (
    <PublicLayout>
      <ContentWrapper>
        <h3 className="text-error">{tError("error_boundary_title")}</h3>
        <p>{tError("error_boundary_description")}</p>
        <FlexContent className="mt-4 justify-start gap-4">
          <Button
            onClick={handleGoHome}
            label={tError("error_boundary_go_home")}
          />
          <Button
            mode="primary"
            label={tError("error_boundary_fill_form")}
            onClick={() => setShowForm(true)}
          />
        </FlexContent>
      </ContentWrapper>
    </PublicLayout>
  );
};

const ErrorBoundary = ({ children }: PropsWithChildren) => {
  return (
    <Sentry.ErrorBoundary
      fallback={(props) => <ErrorFallback {...props} />}
      beforeCapture={(scope) => {
        scope.setTag("error_boundary", "app");
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
